// import { Timestamp } from "@firebase/firestore-types";

import { generateSlug } from "../helpers/Functions";
import Model from "./Model";
import { db } from '../system/firebase/index'
import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where,
} from "firebase/firestore";

// TODO: Eventually this should be a typescript file, so we can use types and auto-complete

// TODO: Add JSdoc types
class Characteristics {
    constructor() {
        this.chassis = {
            number: null,
            visible: null,
        };
        this.code = null;
        this.engine = {
            number: null,
            visible: null,
        };
        this.fileLocation = {
            value: null,
        };
        this.location = {
            value: null,
            visible: null,
        };
        this.make = null;
        this.mileage = {
            unit: null,
            value: null,
        };
        this.model = null;
        this.registration = {
            number: {
                current: null,
                original: null,
                visible: null,
            },
        };
        this.variant = null;
        this.year = null;
    }
}

/**
 * @typedef {Object} Content
 *
 * @property {string} description - Legacy field, usually an HTML string for items made before the page builder
 * @property {string} excerpt - Short description of the item
 * @property {string[]} keypoints - Short bullet points
 * @property {Object[]} modules - The page builder modules
 */
class Content {
    constructor() {
        this.description = null;
        this.excerpt = null;
        this.keypoints = null;
        this.modules = null;
    }
}

/**
 * @typedef {Object} CollectionItemProfile
 *
 * Originally collectionWebProfile fields:
 *
 * @property {string} id
 * @property {string} item_id - The id of the collection item
 * @property {string} title
 * @property {string} slug
 * @property {string} profileName
 * @property {boolean} active // TODO: Why do we have active and visibility?
 * @property {Visibility} visibility
 *
 * @property {string} added_by - The id of the user who added the profile
 * @property {Timestamp} date_added
 * @property {string} edited_by - The id of the user who edited the profile
 * @property {Timestamp} date_modified
 * @property {string} activated_by - The id of the user who activated the profile
 * @property {Timestamp} date_activated
 * @property {string} published_by - The id of the user who published the profile
 * @property {Timestamp} date_published
 *
 * @property {Characteristics} characteristics
 * @property {Content} content
 * @property {Object} media // TODO: What even is this?
 * @property {string[]} tags // TODO: Is this needed?
 *
 * Originally collectionSaleProfile fields:
 *
 * @property {string[]} categories
 * @property {string} saleSeller - The id of the seller (Client)
 * @property {Object} saleSpecialist // The id of the specialist (User)
 * @property {number} salePrice
 * @property {string} saleCurrency - The id of the currency (Currency)
 * @property {boolean} salePriceVisible - Whether to display the sale details on the web
 * @property {?Timestamp} storageDateIn
 * @property {?Timestamp} storageDateOut
 * @property {?string} storageLocation
 * @property {string} notes
 */
class CollectionItemProfile extends Model {
    // static fields
    static table = "collectionItemProfiles";

    static fieldAddedBy = "added_by";
    static fieldAddedDate = "date_added";
    static fieldEditedBy = "edited_by";
    static fieldEditedDate = "date_modified";

    // FIXME: maybe there should be a "fields" property that contains all the fields and their types, defaults, and validation rules
    //  - then swap constructor for a static method that creates a new object with defaults
    //  - but do I still get type hinting? probably not :(
    //  - see 005.dynamic-model-properties-with-type-hinting.txt
    static fieldMetadata = {
        item_id: {
            validationRules: "required|string",
        },
        profile: {
            validationRules: "required|string",
        },
        categories: {
            validationRules: "required|profileCategories",
        }
    };

    // exported enums
    static ProfileType = {
        AVAILABLE: "available",
        NEW_ENTRY: "new-entry",

        SOLD: "sold",
        RECENTLY_SOLD: "recently-sold",

        STORAGE: "storage",
        DATABASE: "database",
    };

    static ProfileCategory = {
        AVAILABLE: "available",
        NEW_ENTRY: "new-entry",

        SOLD: "sold",
        RECENTLY_SOLD: "recently-sold",

        STORAGE: "storage",
        DATABASE: "database",
    };

    static Visibility = {
        DRAFT: "draft",
        PUBLIC: "public",
        PRIVATE: "private",
    };

    static MileageUnit = {
        KM: "km",
        MILES: "miles",
    };

    // TODO: Maybe this constructor should be private, and always create a new one from a static method?
    //  - eg. for setting added_by user, dates, item_id, etc
    constructor() {
        super();

        // TODO: can do assignments in the fromFirestore method
        this.id = null;
        this.item_id = null;
        this.title = null;
        this.slug = null;
        this.profile = null; // TODO: this should really be profile_name, but don't want to mess up other functions for now

        this.active = true; // TODO: Is this even required anymore?
        this.visibility = CollectionItemProfile.Visibility.DRAFT; // TODO: This should be a Visibility enum
        this.added_by = null;
        this.date_added = null; // TODO: This should be a Timestamp object
        this.edited_by = null;
        this.date_modified = null; // TODO: This should be a Timestamp object
        this.activated_by = null;
        this.date_activated = null; // TODO: This should be a Timestamp object
        this.published_by = null;
        this.date_published = null; // TODO: This should be a Timestamp object

        // TODO: Themes use an old, nested object for "characteristics" fields - maybe add a function to convert back to this for themes?
        // this.characteristicsChassisNumber = null; // stored in collectionItem - should we allow overwriting here?
        this.characteristicsChassisNumberVisible = null;
        // this.characteristicsEngineNumber = null; // stored in collectionItem - should we allow overwriting here?
        this.characteristicsEngineNumberVisible = null;
        this.characteristicsRegistrationNumberCurrent = null;
        this.characteristicsRegistrationNumberCurrentVisible = null;
        this.characteristicsLocation = null;
        this.characteristicsLocationVisible = null;
        this.characteristicsFileLocation = null;
        this.characteristicsMileage = null;
        this.characteristicsMileageUnit = null;
        this.content = null; // TODO: This should be a Content object
        this.featuredImage = null; // TODO: This should be a Media object (filepath, filename, etc)
        this.tags = []; // TODO: Is this needed?

        this.categories = []; // TODO: Should this be an enum? Or a Category object from another table? Or array?

        // TODO: Themes use an old, nested object for "sale" fields - maybe add a function to convert back to this for themes?
        this.saleSeller = null;
        this.saleSpecialist = null;
        this.salePrice = null;
        this.saleCurrency = null;
        this.salePriceVisible = null;
        this.storageDateIn = null;
        this.storageDateOut = null;
        this.storageLocation = null;
        this.notes = null;

        // TODO: maybe this shouldn't be part of the main class? it's only used for legacy data
        this.legacyWebProfileCharacteristics = null;
        this.legacyWebProfileMedia = null;
        this.legacyWebProfileLegacyMediaSet = null;
        this.legacySaleProfileId = null;
        this.legacySaleProfileTitle = null;
        this.legacySaleProfileAddedBy = null;
        this.legacySaleProfileActive = null;
        this.legacySaleProfileDateAdded = null;
        this.legacySaleProfileEditedBy = null;
        this.legacySaleProfileDateModified = null;
        this.legacySaleProfileSale = null;
    }

    // TODO: map out default values for all fields (eg. null, or false, or [], or {}, etc)
    //  - is this not just handled in the constructor?

    // TODO: should we have functions for adding auth and date, bumping modified date, etc

    /**
     * Get the single "ProfileType" from an array of ProfileCategory values
     *  - order of preference: AVAILABLE, SOLD, STORAGE, DATABASE, null
     *
     * @param {CollectionItemProfile.ProfileCategory[]} categories
     *
     * @returns {CollectionItemProfile.ProfileType|null}
     */
    static determineProfileTypeFromProfileCategories(categories) {
        if (categories.includes(CollectionItemProfile.ProfileType.AVAILABLE)) {
            return CollectionItemProfile.ProfileType.AVAILABLE;
        } else if (
            categories.includes(CollectionItemProfile.ProfileType.SOLD)
        ) {
            return CollectionItemProfile.ProfileType.SOLD;
        } else if (
            categories.includes(CollectionItemProfile.ProfileType.STORAGE)
        ) {
            return CollectionItemProfile.ProfileType.STORAGE;
        } else if (
            categories.includes(CollectionItemProfile.ProfileType.DATABASE)
        ) {
            return CollectionItemProfile.ProfileType.DATABASE;
        } else {
            return null;
        }
    }

    /**
     * Get the single "Type" of the profile (based on the applied categories)
     *  - order of preference: AVAILABLE, SOLD, STORAGE, DATABASE, null
     *
     * @returns {CollectionItemProfile.ProfileType|null}
     */
    getProfileType() {
        return CollectionItemProfile.determineProfileTypeFromProfileCategories(
            this.categories
        );
    }

    /**
     * Get the human readable label for the "visibility" field
     * 
     * @returns string
     */
    getVisibilityLabel() {
        const mapping = {
            [CollectionItemProfile.Visibility.DRAFT]: "Draft",
            [CollectionItemProfile.Visibility.PUBLIC]: "Public",
            [CollectionItemProfile.Visibility.PRIVATE]: "Private",
        };

        return mapping?.[this.visibility] ?? "";
    }

    /**
     * Get the chassisNumber of this profile (from CollectionItem)
     *  - FIXME: Should we only be able to get this from CollectionItem? Should we have a getCollectionItem function()?
     * 
     * @returns {string|false}
     */
    async getChassisNumber() {
        let output = false
        if (this.item_id) {
            const collectionItem = await getDoc(doc(db, 'collectionItems', this.item_id));

            if (collectionItem) {
                return collectionItem.get('chassisNumber');
            }
        }
        return output
    }

    /**
     * Get the "owner" of this profile (the latest collectionHistory item)
     *  - FIXME: Should this be in CollectionItem? Should it be a model?
     * 
     * @returns {Object|false}
     */
    async getOwner() {
        let output = false
        if (this.item_id) {
            const docRef = query(collection(db, 'collectionHistory'), where('sku', '==', this.item_id), where('current', '==', true))
            const querySnapshot = await getDocs(docRef)
            if (querySnapshot.docs.length) {
                const history = querySnapshot.docs[0].data()
                if (history.owner) {
                    const docClientRef = doc(db, 'clients', history.owner)
                    const docClientSnap = await getDoc(docClientRef)
                    if (docClientSnap.exists) {
                        output = { ...docClientSnap.data(), id: docClientSnap.id }
                    }
                }
            }
        }
        return output
    }

    /**
     * Get the "asking price" of this profile (the formatted price, with a currency symbol)
     *  - FIXME: Should this be elsewhere? Should currencies be ENUMs instead of database?
     * 
     * @returns {string|false}
     */
    async getAskingPrice() {
        let output = false;
        if (this?.salePrice && this?.saleCurrency) {
            const currencyDoc = await getDoc(doc(db, 'currencies', this.saleCurrency));
            if (currencyDoc.exists) {
                const currencySymbol = currencyDoc.get('symbol') || '';
                const outputPrice = typeof this.salePrice === 'number' ? this.salePrice.toLocaleString('en-US') : this.salePrice;
                return `${currencySymbol}${outputPrice}`;
            }
        }
        return output;
    }

    /**
     * Generate a slug for the profile based on the title.
     *  - If a profile with the same slug already exists, append a number to the end until it's unique.
     *
     * @param {Number} count
     *
     * @returns {String} - The generated slug
     */
    async generateProfileSlug(count = 0) {
        const slugified = generateSlug(
            count > 0 ? this.title + `-${count}` : this.title
        );

        const profilesMatchingSlug = await this.constructor.where(
            "slug",
            "==",
            slugified
        );

        if (profilesMatchingSlug.length) {
            count++;
            return this.generateProfileSlug(count);
        }

        return slugified;
    }

    /**
     * Extended from Model.saveToFirestore to handle additional, class-specific requirements
     */
    async saveToFirestore(currentUser) {
        // return certain fields back to default if they're not relevant to the profile type

        const profileType = this.getProfileType();

        if (
            [CollectionItemProfile.ProfileType.STORAGE].includes(
                profileType
            ) === false
        ) {
            this.resetFieldsToDefault([
                "storageDateIn",
                "storageDateOut",
                "storageLocation",
            ]);
        }

        if (
            [
                CollectionItemProfile.ProfileType.AVAILABLE,
                CollectionItemProfile.ProfileType.SOLD,
                CollectionItemProfile.ProfileType.DATABASE,
            ].includes(profileType) === false
        ) {
            this.resetFieldsToDefault([
                "slug",
                "characteristicsChassisNumberVisible",
                "characteristicsEngineNumberVisible",
                "characteristicsRegistrationNumberCurrent",
                "characteristicsRegistrationNumberCurrentVisible",
                "characteristicsLocation",
                "characteristicsLocationVisible",
                "characteristicsFileLocation",
                "characteristicsMileage",
                "characteristicsMileageUnit",
                "content",
                "saleSeller",
                "saleSpecialist",
                "salePrice",
                "saleCurrency",
                "salePriceVisible",
            ]);
        }

        return super.saveToFirestore(currentUser);
    }

    /**
     * Delete this CollectionItemProfile
     *  - this doesn't actually care if the document existed or not (due to Firestore not giving that information)
     * 
     * @returns {boolean} - Successful?
     */
    async delete() {
        if (!this.id) return false;

        try {
            await this.deleteFromFirestore();
            return true;
        } catch {
            // TODO: Maybe handle this?
            return false;
        }
    }

    /**
     * Reset the specified fields to their default values
     *
     * @param {string[]} fieldNames - The names of the fields to reset
     */
    resetFieldsToDefault(fieldNames) {
        const defaultProfile = new CollectionItemProfile();

        fieldNames.forEach((fieldName) => {
            this[fieldName] = defaultProfile[fieldName];
        });
    }
}

export default CollectionItemProfile;
